import { useCallback, memo, useMemo } from 'react'
import {
  Text,
  View,
  ChatComponents,
  PageLayout,
  PageHeader,
  CustomPlaceholder,
} from '@/components'
import { Chat as ChatPackage } from '@/config'
import { useIsMobile } from '@/hooks'
import { APIClient } from '@/services'
import { createStyles } from '@codeleap/styles'
import { useChatFilters, chatSort, channelRenderFilterFn } from '@packages/chat'
import { ChannelList, InfiniteScroll } from 'stream-chat-react'
import { StaticImageData } from 'next/image'

const options = {
  presence: true,
  state: true,
  watch: true,
  limit: 10,
  include_polls: true,
}

export const ChatList = memo(({ onSelect }: { onSelect: (id: string) => void }) => {
  const user = ChatPackage.Store.useUser()
  const { data: profile } = APIClient.Session.useProfile()
  const isMobile = useIsMobile()

  const {
    filters,
    searchText,
    setSearchText,
  } = useChatFilters(user)

  const handleNewChat = useCallback(async () => {
    const channel = await ChatComponents.NewChatModal.request()

    if (!channel) return

    onSelect(channel?.id)
  }, [onSelect])

  const handleChannelSelect = useCallback((channel: any) => {
    onSelect(channel?.id)
  }, [onSelect])

  const renderPreview = useCallback((props: any) => (
    <ChatComponents.ChatPreview
      {...props}
      profile={profile}
      onSelect={handleChannelSelect}
      filters={filters}
    />
  ), [profile, handleChannelSelect, filters])

  const subHeaderStyle = useMemo(() => ['px:2', isMobile ? 'pt:2' : 'pt:3', 'pb:1'] as const, [isMobile])

  return (
    <PageLayout
      style={styles.chats}
      header={
        <PageHeader
          avatarInfoProps={{
            title: profile?.name,
            avatar: profile?.avatar as unknown as StaticImageData,
          }}
          actionIconProps={{
            icon: 'edit',
            onPress: handleNewChat,
          }}
          searchInputProps={{
            value: searchText,
            onValueChange: setSearchText,
            placeholder: 'Search',
            debugName: 'search-chat',
          }}
        />
      }
      subHeader={
        <View style={subHeaderStyle}>
          <Text text='Chats' style={styles.subHeaderTxt} />
        </View>
      }
    >
      <ChannelList
        setActiveChannelOnMount={false}
        filters={filters}
        sort={chatSort}
        channelRenderFilterFn={channelRenderFilterFn}
        options={options}
        Paginator={InfiniteScroll}
        EmptyStateIndicator={EmptyChatPlaceholder}
        Preview={renderPreview}
      />
    </PageLayout>
  )
})

const EmptyChatPlaceholder = memo(() => {
  return (
    <CustomPlaceholder
      iconName={'edit-05'}
      title={'No messages yet'}
      description={"Once you've started messaging, your conversations will appear here."}
    />
  )
})

const styles = createStyles(theme => ({
  chats: {
    height: 'calc(100vh - 48px)',
    overflow: 'hidden',
    ...theme.presets.flex,
    background: `linear-gradient(21.46deg, ${theme.baseColors.primarySolid700} 33.68%, ${theme.baseColors.primarySolid500} 85.89%)`,
    borderBottomLeftRadius: theme.radius.regular,

    [theme.media.down('tablet')]: {
      height: '100vh',
      borderBottomLeftRadius: 0,
    },
  },
  subHeaderTxt: {
    fontSize: 24,
    lineHeight: '26px',
    fontWeight: '700',
    color: theme.colors.textHeadingHighlighted,
  },
}))
